import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { connect, ConnectedProps } from 'react-redux';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import TabsShell, { PathActiveEnum } from 'Components/UI/TabsShell';
import PageHeader from 'Components/UI/PageHeader/PageHeader';
import BreadCrumb, { IBreadcrumbItem } from 'Components/UI/BreadCrumb';
import TableActionTopbar from 'Components/UI/TableActionTopbar';
import ShowCreateActivity from './Modals/ShowCreateActivity';
import KanbanVision from './components/KanbanVision';
import ListVision from './components/ListVision';
import ListButtons, { TypeButton } from './components/ListButtons';
import useOptionsSelect from './Modals/ShowCreateActivity/useOptionsSelect';
import ActivitiesCTAUpgrade from './Modals/ActivitiesCTAUpgrade';
import useActivity from './useActivity';
import UpdateStatus from 'Components/UI/UpdateStatusList';
import ActivitySignalR from 'Components/UI/SignalR/activity';
import ActiveLoading from './components/ActiveLoading';
import Filters from './components/Filters';
import ShareLink from './Modals/ShareLink';
import { AiChatModal } from './Modals/AiChatModal';
import PrintDetailsModal from './Modals/PrintDetails';
import FloatingButton from './components/FloatingButton';
import { HeaderBreadcrumb, Wrapper } from './styles';

export const BUTTON_ACTIVITY_LIST_KEY = 'BUTTON_ACTIVITY_LIST_KEY';

const breadCrumbs: IBreadcrumbItem[] = [
  {
    icon: 'menuHome',
    href: '/',
  },
];

const Activities: FC<Props> = ({
  isCollapsed,
  listActivities,
  totalActivitiesCount: totalActivitiesCountFromStore,
  isLoading,
  isLoadingCreate,
  isLoadingUpdate,
  isLoadingDelete,
  topicStatusOptions,
  topicTypesOptions,
  topicStagesOptions,
  editStage,
  topicPointsOptions,
  topicCommunicationTypesOptions,
  constructionSiteDisciplinesOptions,
  usersOptions,
  externalGlobalLoading,
  userInfo,
  planFree,
  endStatus,
  lastModifiedStatus,
  lastModifiedDate,
  updateStatus,
  topicSignalRHasUpdate,
  getOld,
  obrasList,
  newActivityGuid,
  shareLink,
  isLoadingShareLink,
  filters,
  allowOnlyStagesLinkedToDisciplinesInTopics,
  newEditLoading,
}) => {
  const { t } = useTranslation('activities');
  const params = useParams();

  const {
    activeListButton,
    searchActivitiesDefault,
    height,
    width,
    query,
    showCreateActivity,
    lastModifiedLoading,
    showFilters,
    showFiltersShare,
    hasFilters,
    firstRenderFilters,
    lastFilterUpdateId,
    blockFilterDropdown,
    inAppTopicNotifications,
    showAiModal,
    showPrintDetailsModal,
    filteredActivitiesCount,
    totalActivitiesCount,
    moreActionsType,
    handleSetMoreActionsType,
    toggleAiModal,
    handleClosePrintDetailsModal,
    handleConfirmPrintDetailsModal,
    handleActiveListButton,
    upgradePlanRedirect,
    getActionsToolbar,
    setSearchActivities,
    setSearchLoading,
    handleOpenCreateActivity,
    handleOpenShowActivity,
    handleDuplicateActivity,
    handleCloseShowActivity,
    handleCloseOpenCreateActivity,
    handleOpenActivityViewpoint,
    handleShowFiltersShare,
    addFilter,
    updateFilter,
    deleteFilter,
    getUpdatedActivity,
  } = useActivity({
    constructionSiteId: Number(params.constructionSiteId),
    listActivities,
    totalActivitiesCount: totalActivitiesCountFromStore,
    topicSignalRHasUpdate,
    userInfo,
    externalGlobalLoading,
    obrasList,
    shareLink,
    filters,
  });

  const {
    visibilityOptions,
    priorityOptions,
    priorityNoBgOptions,
    statusOptions,
    typeOptions,
    stageOptions,
    pointsOptions,
    communicationOptions,
    userOptions,
    disciplineOptions,
  } = useOptionsSelect({
    topicStatusOptions,
    topicTypesOptions,
    topicStagesOptions,
    topicPointsOptions,
    topicCommunicationTypesOptions,
    constructionSiteDisciplinesOptions,
    usersOptions,
  });

  const dontHaveActivitiesAccess =
    planFree ||
    (userInfo?.UserStoreItem.StorePlan?.Order &&
      userInfo?.UserStoreItem.StorePlan?.Order < 3);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        toggleAiModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleAiModal]);

  if (params?.constructionSiteId) {
    const currentObra = obrasList?.find((obra) => obra.ConstructionSiteId === Number(params.constructionSiteId));

    breadCrumbs[0] = {
      href: `/obras/detail/${params.constructionSiteId}`,
      description: currentObra?.Name || t('page.defaultObra'),
    };
    breadCrumbs[1] = {
      href: `/obras/activities/detail/${params.constructionSiteId}`,
      description: t('page.breadcrumb'),
    };
  }

  const buttons: TypeButton = {
    kanban: {
      nameId: 'kanban',
      placement: t('page.buttonKanban'),
      icon: 'listKanban',
      iconSize: 16,
    },
    list: {
      nameId: 'list',
      placement: t('page.buttonList'),
      icon: 'listList',
      iconSize: 16,
    },
    // será implementado posteriormente
    // 'detailVision': {
    //   nameId: 'detailVision',
    //   placement: 'Lista Detalhada',
    //   icon: 'listDetailVision',
    //   iconSize: 16,
    // },
  };

  const isAllLoading = isLoading || externalGlobalLoading;
  const showKanban = !isAllLoading && activeListButton === 'kanban';
  const showList = !isAllLoading && activeListButton === 'list';
  // const showDetailVision = !isLoading && activeListButton === 'detailVision';

  return (
    <Wrapper isCollapsed={isCollapsed}>
      <ActivitySignalR csId={Number(params.constructionSiteId)} />
      <TabsShell
        ConstructionSiteId={params.constructionSiteId}
        active={PathActiveEnum.activities}
        loading={false}
      />
      <PageHeader name={t('page.title')}>
        <HeaderBreadcrumb>
          <BreadCrumb breadcrumbs={breadCrumbs} />
          <UpdateStatus
            status={lastModifiedStatus}
            date={lastModifiedDate}
            loading={isLoading && !lastModifiedLoading}
            custom
          />
        </HeaderBreadcrumb>
        {dontHaveActivitiesAccess && (
          <ActivitiesCTAUpgrade
            onConfirm={upgradePlanRedirect}
            planFree={!planFree}
            order={userInfo?.UserStoreItem.StorePlan?.Order}
            role={userInfo?.CurrentRoleFk}
          />
        )}
        {!dontHaveActivitiesAccess && (
          <>
            <div className='headerWrapper'>
              <TableActionTopbar
                buttons={getActionsToolbar() || []}
                toggleAiModal={toggleAiModal}
                defaultValue={searchActivitiesDefault}
                onSearch={setSearchActivities}
                onSearchLoading={setSearchLoading}
                inputPlaceholder={t('page.searchPlaceholder')}
                inputClassName={'inputTopBar'}
                LeftComponent={
                  <ListButtons
                    storageKey={BUTTON_ACTIVITY_LIST_KEY}
                    buttons={buttons}
                    activeButton={activeListButton}
                    onActiveOption={handleActiveListButton}
                  />
                }
                showFilters={showFilters}
              />
              {showFilters && (
                <Filters
                  filters={filters}
                  addFilter={addFilter}
                  updateFilter={updateFilter}
                  deleteFilter={deleteFilter}
                  firstRenderFilters={firstRenderFilters}
                  lastFilterUpdateId={lastFilterUpdateId}
                  blockFilterDropdown={blockFilterDropdown}
                />
              )}
            </div>
            <div className='mainWrapper'>
              {isAllLoading && (
                <ActiveLoading activeListButton={activeListButton} height={height} />
              )}
              {showKanban && (
                <KanbanVision
                  csId={Number(params.constructionSiteId)}
                  listActivities={listActivities}
                  height={height}
                  searchActivity={query.get('searchActivity') || ''}
                  endStatus={endStatus}
                  showFilters={showFilters}
                  filters={filters}
                  hasFilters={hasFilters}
                  inAppTopicNotifications={inAppTopicNotifications}
                  onOpenCreateActivity={handleOpenCreateActivity}
                  onOpenShowActivity={handleOpenShowActivity}
                />
              )}
              {showList && (
                <ListVision
                  csId={Number(params.constructionSiteId)}
                  listActivities={listActivities}
                  height={height}
                  width={width}
                  searchActivity={query.get('searchActivity') || ''}
                  endStatus={endStatus}
                  showFilters={showFilters}
                  filters={filters}
                  hasFilters={hasFilters}
                  inAppTopicNotifications={inAppTopicNotifications}
                  onOpenCreateActivity={handleOpenCreateActivity}
                  onOpenShowActivity={handleOpenShowActivity}
                />
              )}
            </div>
          </>
        )}
      </PageHeader>

      {showCreateActivity && (
        <ShowCreateActivity
          visible={showCreateActivity}
          loading={isLoadingCreate}
          loadingExternalLink={isAllLoading}
          loadingDelete={isLoadingDelete}
          loadingUpdate={isLoadingUpdate}
          editStage={editStage}
          title={t('page.newActivity')}
          csId={Number(params.constructionSiteId)}
          topicStatus={topicStatusOptions}
          topicStages={topicStagesOptions}
          topicPoints={topicPointsOptions}
          topicCommunicationType={topicCommunicationTypesOptions}
          updateActivity={getUpdatedActivity()}
          getOld={getOld}
          visibilityOptions={visibilityOptions}
          priorityOptions={priorityOptions}
          priorityNoBgOptions={priorityNoBgOptions}
          statusOptions={statusOptions}
          typeOptions={typeOptions}
          stageOptions={stageOptions}
          pointsOptions={pointsOptions}
          communicationOptions={communicationOptions}
          userOptions={userOptions}
          disciplineOptions={disciplineOptions}
          disciplines={constructionSiteDisciplinesOptions}
          updateStatus={updateStatus}
          allowOnlyStagesLinkedToDisciplinesInTopics={
            allowOnlyStagesLinkedToDisciplinesInTopics
          }
          createStageLoading={newEditLoading}
          newActivityGuid={newActivityGuid}
          userInfo={userInfo}
          moreActionsType={moreActionsType}
          isActivityPage
          onSetMoreActionsType={handleSetMoreActionsType}
          onCloseOpenCreateActivity={handleCloseOpenCreateActivity}
          onOpenShowActivity={handleOpenShowActivity}
          onDuplicateActivity={handleDuplicateActivity}
          onOpenViewpoint={handleOpenActivityViewpoint}
          onCancel={() => handleCloseShowActivity()}
        />
      )}
      {showFiltersShare && (
        <ShareLink
          visible={showFiltersShare}
          constructionSiteId={Number(params.constructionSiteId)}
          context={userInfo?.CurrentTenantContext}
          shareLink={shareLink}
          loading={isLoadingShareLink}
          onCancel={() => handleShowFiltersShare(false)}
        />
      )}

      {showPrintDetailsModal && (
        <PrintDetailsModal
          visible={showPrintDetailsModal}
          onCancel={handleClosePrintDetailsModal}
          onPrint={handleConfirmPrintDetailsModal}
          hasFiltersApplied={hasFilters}
          filteredActivitiesCount={filteredActivitiesCount}
          totalActivitiesCount={totalActivitiesCount}
        />
      )}

      <FloatingButton onClick={toggleAiModal} />

      <AiChatModal
        showAiModal={showAiModal}
        toggleAiModal={toggleAiModal}
        constructionSiteId={Number(params.constructionSiteId)}
      />
    </Wrapper>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.activities,
  isCollapsed: state.obra.isCollapsed,
  userInfo: state.auth.userInfo,
  planFree: state.auth.planFree,
  obrasList: state.obra.ObrasList,
  disciplines: state.obra.Obra.Disciplines,
  allowOnlyStagesLinkedToDisciplinesInTopics:
    state.obra.Obra.AllowOnlyStagesLinkedToDisciplinesInTopics,
  newEditLoading: state.obra.newEditLoading,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(Activities);
